import { useGetProcessorJobStagesQuery } from '@consigli/hooks';
import { ProcessorJobStage, ProcessorJobStageStatus, StatusVariant } from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState, useCallback } from 'react';

import { Badge, DataTable } from '@/components';
import { formatTimestamp } from '@/utils/timestamp-converting';

import { TasksSheet } from './tasks-sheet';

const stageStatusMapping = {
  [ProcessorJobStageStatus.INITIALIZED]: { text: 'Initialized', variant: StatusVariant.INFO },
  [ProcessorJobStageStatus.STARTED]: { text: 'Started', variant: StatusVariant.INFO },
  [ProcessorJobStageStatus.FAILED]: { text: 'Failed', variant: StatusVariant.ERROR },
  [ProcessorJobStageStatus.COMPLETED]: { text: 'Completed', variant: StatusVariant.SUCCESS },
} as const;

interface StagesExpandedContentProps {
  projectId: number;
  jobId: string;
}

export const StagesExpandedContent = ({ projectId, jobId }: StagesExpandedContentProps) => {
  const { data: stages = [], isLoading } = useGetProcessorJobStagesQuery({
    projectId,
    jobId,
  });

  const [selectedStage, setSelectedStage] = useState<ProcessorJobStage | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const handleStageClick = useCallback((stage: ProcessorJobStage) => {
    setSelectedStage(stage);
    setIsSheetOpen(true);
  }, []);

  const handleSheetClose = useCallback((open: boolean) => {
    setIsSheetOpen(open);
    if (!open) {
      setSelectedStage(null);
    }
  }, []);

  const columns: ColumnDef<ProcessorJobStage>[] = useMemo(
    () => [
      {
        header: 'Stage Name',
        accessorKey: 'stageName',
        cell: ({ getValue }) => {
          const stageName = getValue() as string;
          return <span>{formatEnumToReadableText(stageName)}</span>;
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }) => {
          const status = getValue() as ProcessorJobStageStatus;
          const config = stageStatusMapping[status];
          return <Badge text={config.text} variant={config.variant} />;
        },
      },
      {
        header: 'Task Progress',
        accessorFn: (row) => {
          const percentage =
            row.totalTasks === 0 ? 0 : Math.round((row.completedTasks / row.totalTasks) * 100);
          return `${row.completedTasks}/${row.totalTasks} (${percentage}%)`;
        },
      },
      {
        header: 'Failed Tasks',
        accessorKey: 'totalFailedTasks',
      },
      {
        header: 'Created At',
        accessorKey: 'createdAt',
        cell: ({ getValue }) => {
          const createdAt = getValue() as string;
          return <span>{formatTimestamp(createdAt)}</span>;
        },
      },
      {
        header: 'Updated At',
        accessorKey: 'updatedAt',
        cell: ({ getValue }) => {
          const updatedAt = getValue() as string;
          return <span>{formatTimestamp(updatedAt)}</span>;
        },
      },
    ],
    [],
  );

  return (
    <div className="pl-4">
      <DataTable
        columns={columns}
        data={stages}
        isLoading={isLoading}
        onRowClick={handleStageClick}
      />

      {selectedStage && (
        <TasksSheet
          projectId={projectId}
          jobId={jobId}
          stage={selectedStage}
          isOpen={isSheetOpen}
          onOpenChange={handleSheetClose}
        />
      )}
    </div>
  );
};
